import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { when } from "lit/directives/when.js";
import "prismjs/prism.js";
import "prismjs/components/prism-csharp";
import "prismjs/components/prism-cshtml";
import { apiBaseUrl } from "../constants";
import { baseStyles, prismStyles } from "../styles";
import {
  fetchApi,
  urlPathToNugetId,
  dominantColorGenerator,
  AppClassToggle,
} from "../functions";
import { StateController } from "../state";
import type { Package, SidebarRenderMode } from "../components";

@customElement("umb-market-package")
export class UmbMarketPackage extends LitElement {
  @property({ type: Object })
  package!: Package;

  #highlighted = false;

  #longSidebarThreshold = 1000;

  // these are included by default, mustache doesn't exist
  #ignoredLanguages = ["html", "css", "js", "javascript", "mustache"];

  async connectedCallback() {
    super.connectedCallback();

    // when path is /embed/
    if (!this.package) {
      const query = urlPathToNugetId(window.location.pathname);

      const packageResult = await fetchApi<Package>(
        `${apiBaseUrl()}packages/${query}`
      );

      if (!packageResult) return;

      if (StateController.isEmbedView) {
        AppClassToggle("embed", "add");
        this.classList.add("embed");

        // also add canonical meta, just for crawler friendliness
        const tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        tag.href = `${window.location.origin}/package/${query}`;
        document.head.appendChild(tag);
      }

      dominantColorGenerator(this, packageResult.iconDominantColor);
      this.package = packageResult;
    }
  }

  #getAwardsInfo() {
    if (
      this.package.isRetired ||
      this.package.isHQ ||
      !this.package.awards.length
    ) {
      return null;
    }

    return html`
      <umb-package-awards .awards=${this.package.awards}></umb-package-awards>
    `;
  }

  #readMeContent() {
    // why does this work? it modifies the rendered element,
    // which hasn't yet been rendered..
    this.#highlight();

    return html`<article id="readme">
      ${this.#getAwardsInfo()}
      ${when(
        this.package.readMeContent,
        () => html`${unsafeHTML(this.package.readMeContent)}`
      )}
      ${when(
        (this.package.readMeContent?.length ?? 0) < this.#longSidebarThreshold,
        () => this.#renderSidebarContent("short-content")
      )}
    </article>`;
  }

  async #highlight() {
    if (this.#highlighted) return;

    const readme = this.shadowRoot?.getElementById("readme");
    if (!readme) return;

    // load languages - import any where the code element has a class language-x
    // if the import fails, catch the failure but don't panic - Prism will fall back
    // to the default highlighting.
    new Set(
      Array.from(readme.querySelectorAll('code[class*="language-"]'))
        .map((n) => n.classList.value.replace("language-", ""))
        .filter((x) => !this.#ignoredLanguages.includes(x))
    ).forEach(async (l) => {
      await import(
        /* @vite-ignore */ `prismjs/components/prism-${l}.min.js`
      ).catch((_) => _);
    });

    window.Prism.highlightAllUnder(readme);
    this.#highlighted = true;
  }

  #renderSidebarContent(variant?: SidebarRenderMode) {
    if (!variant) return null;

    return html` <umb-market-package-sidebar
      variant=${variant}
      .package=${this.package}
    ></umb-market-package-sidebar>`;
  }

  render() {
    if (!this.package) return null;

    return html`
      <div id="content">
        <umb-market-package-card
          .package=${this.package}
        ></umb-market-package-card>
        ${this.#readMeContent()}
      </div>
      <div id="sidebar">
        <umb-market-author-details
          .name=${this.package.authors}
          .owners=${this.package.owners}
          ?isHQ=${this.package.isHQ}
          .authorDetails=${this.package.authorDetails}
        ></umb-market-author-details>
        ${when(
          (this.package.readMeContent?.length ?? 0) <
            this.#longSidebarThreshold,
          () => this.#renderSidebarContent("short-sidebar"),
          () => this.#renderSidebarContent("full-sidebar")
        )}
        ${when(
          (this.package.lookingForContributors ||
            this.package.lookingForMaintainer) &&
            this.package.sourceCodeRepositoryUrl,
          () => html` <umb-market-package-contributors .package=${this.package}>
          </umb-market-package-contributors>`
        )}

        <a
          id="report"
          href=${`mailto:packages@umbraco.com?subject=Package report - ${this.package.title}`}
          >Report package</a
        >
      </div>
      ${when(
        this.package.screenshots.length || this.package.videoUrl,
        () =>
          html`<umb-market-carousel
            .screenshots=${this.package.screenshots}
            .videoUrl=${this.package.videoUrl}
          ></umb-market-carousel>`
      )}
    `;
  }

  static styles = [
    baseStyles,
    prismStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        background: var(--color-body-surface);
        margin: 0 auto;
        position: relative;
        padding: var(--xl-spacer) var(--s-spacer) 0;

        display: grid;
        grid-auto-flow: dense;
        row-gap: var(--l-spacer);
        grid-template-columns: [c1] 100%;
        grid-template-rows: [r1] auto [r2] auto [r3] auto;

        --card-x-padding: 0;
        --l-spacer-double: calc(2 * var(--l-spacer));
      }

      :host(.embed) {
        max-width: none;
        padding: var(--s-spacer) var(--s-spacer) 0;
      }

      :host(.embed) #content {
        margin-top: 0;
      }

      @media (min-width: 767px) {
        :host {
          padding: var(--xl-spacer) var(--l-spacer) 0;
        }

        :host(.embed) {
          max-width: none;
          padding: var(--l-spacer) var(--l-spacer) 0;
        }
      }

      @media (min-width: 1200px) {
        :host {
          max-width: calc(var(--max-width) + var(--l-spacer-double));
          grid-template-columns: [c1] 60% [c2] calc(40% - var(--xl-spacer));
          grid-template-rows: [r1] auto [r2] auto;
          grid-column-gap: var(--xl-spacer);
          grid-row-gap: var(--xl-spacer);
        }
      }

      #content {
        z-index: 2;
        margin: 0 auto;
        margin-top: -80px;
      }

      /* 124 sets the package icon halfway out of the modal */
      @media (min-width: 1200px) {
        #content {
          margin-top: -124px;
        }
      }

      #content:has(#readme) umb-market-package-card {
        --border-bottom-radius: 0;
      }

      article {
        max-width: 100%;
        padding: var(--m-spacer);
        padding-top: 0;

        background-color: white;
        border-bottom-left-radius: 22px;
        border-bottom-right-radius: 22px;
      }

      @media (min-width: 767px) {
        article {
          padding: var(--xl-spacer);
          padding-top: 0;
        }
      }

      article a {
        color: var(--uui-color-default);
      }

      article img {
        width: auto;
        max-width: 100%;
      }

      article:not(:has(umb-package-awards)) > *:first-child,
      article:has(umb-package-awards) > *:nth-child(2) {
        margin-top: 0;
      }

      article blockquote {
        display: inline-flex;
        flex-direction: column;
        margin-left: 0;
        padding-left: 1em;
        border-left: 4px solid var(--dominant-color-opaque);

        > *:last-child {
          margin-bottom: inherit;
        }
      }

      umb-package-awards {
        margin-bottom: var(--m-spacer);
        display: block;
      }

      @media (min-width: 767px) {
        umb-package-awards {
          float: right;
          margin-left: var(--m-spacer);
        }
      }

      pre {
        border-radius: var(--xs-spacer);
        background: white !important;
        padding: 1em;
        margin: 0.5em 0;
        overflow: auto;
      }

      article table {
        border-collapse: collapse;
        width: 100%;
        margin: 0.5em 0;
      }

      article th,
      article td {
        padding: var(--xs-spacer) var(--s-spacer);
        border: 1px solid var(--color-body-border, #ddd);
        text-align: left;
      }

      article th {
        background-color: var(--color-body-surface-alt, #f5f5f5);
      }

      /**
      Grid position for child components */

      #content {
        grid-row: r1 / r1;
        width: 100%;
      }

      umb-market-carousel {
        grid-row: r2 / r2;
        margin: var(--m-spacer) auto var(--s-spacer);
      }

      @media (min-width: 1200px) {
        #content {
          grid-column: c1 / c1;
          grid-row: r1 / r1;
        }

        #sidebar {
          grid-column: c2 / c2;
          grid-row: r1 / r1;
        }

        umb-market-carousel {
          grid-column: c1 / -1;
          grid-row: r2 / r2;
          margin-top: 0;
          margin-bottom: 0;
        }
      }

      /**
      Clean up padding when embedded */
      @media (min-width: 767px) {
        :host(.embed) {
          padding: var(--xl-spacer) var(--xl-spacer) 0;
        }
      }

      #report {
        display: flex;
        justify-content: end;
        color: currentColor;
        font-size: 14px;
      }

      umb-market-author-details + #report,
      umb-market-package-sidebar + #report {
        margin-top: var(--s-spacer);
      }
    `,
  ];
}
